import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SlButtonComponent } from '../sl-button/sl-button.component';
import { SlCheckboxMenuItemComponent } from '../sl-checkbox-menu-item/sl-checkbox-menu-item.component';
import { SlSearchComponent } from '../sl-search/sl-search.component';
import {
  FilterConfig,
  FilterResult,
} from '../sl-filter/interfaces/sl-filter-interfaces';
import { SlTagComponent } from '../sl-tag/sl-tag.component';
import {
  TagColorVariants,
  TagSize,
} from '../utils/global-interfaces/sl-global.interfaces';
import { SlTypographyComponent } from '../sl-typography/sl-typography.component';
import {
  CheckboxInput,
  CheckboxOutput,
} from '../sl-checkbox-menu-item/interfaces/sl-checkbox-interfaces';

interface FilterConfigExtended {
  [key: string]: {
    values: string[];
    isCollapsable: boolean;
    isOpen: boolean;
  };
}

@Component({
  selector: 'sl-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SlButtonComponent,
    SlSearchComponent,
    SlCheckboxMenuItemComponent,
    SlTagComponent,
    SlTypographyComponent,
  ],
  templateUrl: './sl-filter.component.html',
  styleUrl: './sl-filter.component.scss',
})
export class SlFilterComponent implements OnInit {
  @Input() filterConfig: FilterConfig = {};
  @Output() filters: EventEmitter<FilterResult> =
    new EventEmitter<FilterResult>();

  @ViewChild('headerButton') headerButtons!: QueryList<ElementRef>;
  @ViewChild('itemsContainer') suggestionsContainer!: ElementRef;

  readonly cdRef = inject(ChangeDetectorRef);

  parsedFilterConfig: FilterConfigExtended = {};
  openedElementKey = '';

  tagcolorVariants = TagColorVariants;
  tagSize = TagSize;
  Object = Object;

  selectedFilters = new Map<string, string[]>();
  elementsMap = new Map<string, CheckboxInput[]>();
  filteredMap = new Map<string, CheckboxInput[]>();
  isOpen = false;
  showResetButton = false;
  elementIdList: string[] = [];
  selectedElementIndex = 0;
  ngOnInit() {
    this.initializeData();
  }

  initializeData() {
    this.Object.keys(this.filterConfig).forEach((key) => {
      this.elementsMap.set(key, this.filterConfig[key].values);
      this.filteredMap.set(key, this.filterConfig[key].values);
    });
    this.setupElementArray();
  }

  setupElementArray(section?: string) {
    this.elementIdList = [];
    if (!section) {
      this.elementIdList.push('sl-filter-search');
      this.Object.keys(this.filterConfig).forEach((key) => {
        if (this.filterConfig[key].isCollapsable) {
          this.elementIdList.push('sl-filter-forward-button-' + key);
        } else {
          this.filterConfig[key].values.forEach((checkbox) => {
            this.elementIdList.push(
              'sl-filter-checkbox-' + key + '-' + checkbox.name
            );
          });
        }
      });
    } else {
      this.elementIdList.push(
        'sl-filter-back-button-' + section,
        'sl-filter-search'
      );
      this.filteredMap.get(section)?.forEach((checkbox) => {
        this.elementIdList.push(
          'sl-filter-checkbox-' + section + '-' + checkbox.name
        );
      });
    }
    this.elementIdList.push(
      'sl-filter-clear-all-button',
      'sl-filter-cancel-button',
      'sl-filter-apply-button'
    );
  }

  clearMapData() {
    this.Object.keys(this.filterConfig).forEach((key) => {
      const emptyMap = this.elementsMap.get(key)?.map((element) => {
        return {
          name: element.name,
          value: undefined,
        };
      });
      const emptyFilteredMap = this.filteredMap.get(key)?.map((element) => {
        return {
          name: element.name,
          value: false,
        };
      });
      if (emptyMap && emptyFilteredMap) {
        this.elementsMap.set(key, emptyMap);
        this.filteredMap.set(key, emptyFilteredMap);
      }
    });
  }
  handleOpenActionsMenu(open: boolean) {
    this.isOpen = open;
  }

  getCheckboxItems(item: string): CheckboxInput[] {
    const filteredResults = this.filteredMap?.get(item);
    if (filteredResults) {
      return filteredResults;
    } else {
      return [];
    }
  }

  onFocusoutSectionButton(event: FocusEvent) {
    event.stopPropagation();
  }

  onFocusout(event: FocusEvent) {
    event.stopPropagation();
    const relatedTarget = event.relatedTarget as Element;

    if (!relatedTarget?.id.startsWith('sl-filter')) {
      this.isOpen = false;
      this.selectedElementIndex = 0;
    }
  }

  onChangeGlobalSearch(filterValue: string) {
    const mapKeys = Array.from(this.elementsMap.keys());
    mapKeys.forEach((key) => {
      if (!this.filterConfig[key].isCollapsable) {
        const matchingElements: CheckboxInput[] = [];
        this.elementsMap.get(key)?.forEach((element) => {
          if (element.name.toLowerCase().includes(filterValue.toLowerCase())) {
            matchingElements.push(element);
          }
        });
        this.filteredMap.set(key, matchingElements ?? []);
      }
    });
  }

  onChangeItemSearch(filterValue: string, itemName: string) {
    const matchingElements: CheckboxInput[] = [];

    this.elementsMap.get(itemName)?.forEach((checkboxValue) => {
      if (
        checkboxValue.name.toLowerCase().includes(filterValue.toLowerCase())
      ) {
        matchingElements.push(checkboxValue);
      }
    });
    this.filteredMap.set(itemName, matchingElements ?? []);
  }

  onClickForwardSection(selectedElement: string) {
    this.openedElementKey = selectedElement;
    this.setupElementArray(selectedElement);
    this.selectedElementIndex = 0;
    this.filteredMap = new Map(this.elementsMap);

    this.cdRef.detectChanges();
    this.focusOnElement('sl-filter-back-button-' + selectedElement);
  }

  onClickBackSection() {
    this.openedElementKey = '';
    this.setupElementArray();
    this.selectedElementIndex = 0;
    this.filteredMap = new Map(this.elementsMap);

    this.cdRef.detectChanges();
    this.focusOnElement('sl-filter-search');
  }

  onSelectCheckbox(item: string, selectedOptions: CheckboxOutput[]) {
    this.elementsMap.set(item, selectedOptions);
    this.filteredMap.set(item, selectedOptions);
    if (!this.showResetButton) {
      this.showResetButton = true;
    }

    if (this.isMapEmpty()) {
      this.showResetButton = false;
    }
  }
  onClickCheckbox(item: string, element: HTMLElement | null) {
    this.selectedElementIndex =
      this.elementIdList.indexOf(
        'sl-filter-checkbox-' + item + '-' + element?.innerText
      ) ?? 0;
  }

  onClickSearch() {
    this.selectedElementIndex =
      this.elementIdList.indexOf('sl-filter-search') ?? 0;
  }
  isMapEmpty() {
    const mapKeys = Array.from(this.elementsMap.keys());

    const boolArray = mapKeys.map((key) =>
      this.elementsMap.get(key)?.every((checkbox) => !checkbox.value)
    );
    return boolArray.every((element) => element);
  }

  countSelectedElements(key: string) {
    const selectedCheckboxes = this.elementsMap
      .get(key)
      ?.filter((element) => element.value);
    if (selectedCheckboxes) {
      return selectedCheckboxes.length;
    }
    return 0;
  }

  clearAllFilters() {
    this.clearMapData();
    this.showResetButton = false;
    this.selectedElementIndex = 0;
  }

  applyFilters() {
    const parsedObj: { [key: string]: string[] } = {};
    const keys = Array.from(this.elementsMap.keys());
    keys.forEach((key) => {
      const res = this.elementsMap
        .get(key)
        ?.filter((element) => element.value)
        .map((element) => element.name);
      if (res) {
        parsedObj[key] = res;
      }
    });

    this.filters.emit(parsedObj);
    this.isOpen = false;
    document.getElementById('filter-button')?.focus();
  }

  onKeyDownMenu(event: KeyboardEvent) {
    const isArrowDown = event.key === 'ArrowDown';
    const isArrowUp = event.key === 'ArrowUp';
    const isEscape = event.key === 'Escape';
    const isTab = event.key === 'Tab';

    if (isArrowDown || isArrowUp) {
      event.preventDefault();
      this.handleArrowNavigation(isArrowDown);
    } else if (isTab) {
      this.handleTabNavigation(event.shiftKey);
    } else if (isEscape) {
      this.handleEscape();
    }
  }

  onKeyDownMainButton(event: KeyboardEvent) {
    const isArrowDown = event.key === 'ArrowDown';
    const isEscape = event.key === 'Escape';

    if (isArrowDown) {
      event.preventDefault();
      this.focusOnElement(this.elementIdList[0]);
    }
    if (isEscape) {
      this.handleEscape();
    }
  }

  handleTabNavigation(shiftKey: boolean) {
    if (shiftKey) {
      this.selectedElementIndex =
        (this.selectedElementIndex - 1 + this.elementIdList.length) %
        this.elementIdList.length;
    } else {
      this.selectedElementIndex =
        (this.selectedElementIndex + 1) % this.elementIdList.length;
    }
    if (this.shouldSkipCurrentElement()) {
      this.handleTabNavigation(shiftKey);
    }
  }

  handleArrowNavigation(isDown: boolean) {
    const increment = isDown ? 1 : -1;
    const totalItems = this.elementIdList.length;
    const nextIndex =
      (this.selectedElementIndex + increment + totalItems) % totalItems;
    this.selectedElementIndex = nextIndex;
    if (this.shouldSkipCurrentElement()) {
      this.handleArrowNavigation(isDown);
      return;
    }
    this.focusOnElement(this.elementIdList[this.selectedElementIndex]);
  }

  shouldSkipCurrentElement() {
    return (
      this.elementIdList[this.selectedElementIndex] ===
        'sl-filter-clear-all-button' && this.isMapEmpty()
    );
  }

  handleEscape() {
    this.isOpen = false;
    this.focusOnElement('filter-button');
  }

  focusOnElement(elementId: string) {
    document.getElementById(elementId)?.focus();
  }
}
